import { useCallback, useEffect, useRef, useState } from "react";

import { UnstyledButton } from "~/components/mantine/button/Button";

import styles from "./WelcomeVideo.module.scss";

// Autoplay waits for the page to settle and for the player to be in sight.
const AUTOPLAY_DELAY_MS = 1500;
const AUTOPLAY_VISIBLE_SHARE = 0.25;

type Props = {
  src: string;
  poster?: string;
  caption: string;
  playLabel: string;
  // Off once the video has done its job: the cover waits for a tap.
  autoplay: boolean;
  loop?: boolean;
  // A tap of the cover or the sound turned on: the user is watching.
  onWatch: () => void;
  // Any start, the muted autoplay included.
  onPlay?: () => void;
};

/**
 * A video as a lazy facade: it has no source until it starts, so nothing
 * downloads with the page. It starts muted (browsers only autoplay muted
 * videos) once the page has loaded and the player is in sight; if the
 * browser blocks that, the play cover comes back.
 *
 * A muted autoplay isn't watching: `onWatch` fires when the user plays or
 * unmutes it.
 */
const LazyVideo = ({
  src,
  poster,
  caption,
  playLabel,
  autoplay,
  loop = true,
  onWatch,
  onPlay,
}: Props) => {
  const playerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isCoverHidden, setIsCoverHidden] = useState(false);

  const startPlayback = useCallback(() => {
    const video = videoRef.current;

    if (!video) return;

    // Safari checks the property, not the attribute, before autoplaying.
    video.muted = true;
    video.src = src;
    video.preload = "metadata";
    setIsCoverHidden(true);

    video.play().catch(() => {
      // Autoplay blocked: back to the facade, and stop the download.
      video.preload = "none";
      video.removeAttribute("src");
      video.load();
      setIsCoverHidden(false);
    });
  }, [src]);

  useEffect(() => {
    const player = playerRef.current;

    if (!player || !autoplay) return;

    let isVisible = false;
    let isPageReady = false;
    let isStarted = false;
    let timeoutId: number | undefined;

    // One attempt only: a blocked autoplay leaves the cover to the user.
    const tryAutoplay = () => {
      if (isStarted || !isVisible || !isPageReady) return;

      isStarted = true;
      startPlayback();
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.intersectionRatio >= AUTOPLAY_VISIBLE_SHARE;
        tryAutoplay();
      },
      { threshold: AUTOPLAY_VISIBLE_SHARE },
    );
    observer.observe(player);

    const handlePageLoad = () => {
      timeoutId = window.setTimeout(() => {
        isPageReady = true;
        tryAutoplay();
      }, AUTOPLAY_DELAY_MS);
    };

    // The dashboard usually mounts after the page load event.
    if (document.readyState === "complete") {
      handlePageLoad();
    } else {
      window.addEventListener("load", handlePageLoad, { once: true });
    }

    return () => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
      window.removeEventListener("load", handlePageLoad);
    };
  }, [startPlayback, autoplay]);

  const handleCoverClick = () => {
    onWatch();
    startPlayback();
  };

  const handleVolumeChange = () => {
    if (videoRef.current && !videoRef.current.muted) onWatch();
  };

  return (
    <section className={styles.card}>
      <div ref={playerRef} className={styles.player}>
        <video
          ref={videoRef}
          className={styles.video}
          controls
          muted
          loop={loop}
          playsInline
          preload="none"
          poster={poster}
          onVolumeChange={handleVolumeChange}
          onPlay={onPlay}
        />
        {!isCoverHidden && (
          <div className={styles.cover}>
            {poster && <img className={styles.poster} src={poster} alt="" />}
            <UnstyledButton
              className={styles.playButton}
              aria-label={playLabel}
              onClick={handleCoverClick}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden>
                <path d="M8 5v14l11-7z" fill="currentColor" />
              </svg>
            </UnstyledButton>
          </div>
        )}
      </div>
      <div className={styles.caption}>{caption}</div>
    </section>
  );
};

export default LazyVideo;
